import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Trophy,
  Users,
  FileText,
  TrendingUp,
  Award,
  Flame,
  Star,
  Globe,
  BarChart3,
  Activity,
} from 'lucide-react';
import { api } from '../../services/api';
import { TimeAgo } from '../../components/admin/shared';

type Tab = 'gamification' | 'engagement' | 'audit';

export default function UserInsights() {
  const [activeTab, setActiveTab] = useState<Tab>('gamification');

  const { data: gamification, isLoading: gamificationLoading } = useQuery({
    queryKey: ['insights-gamification'],
    queryFn: () => api.getInsightsGamification(),
    enabled: activeTab === 'gamification',
  });

  const { data: engagement, isLoading: engagementLoading } = useQuery({
    queryKey: ['insights-engagement'],
    queryFn: () => api.getInsightsEngagement(),
    enabled: activeTab === 'engagement',
  });

  const { data: auditLog, isLoading: auditLoading } = useQuery({
    queryKey: ['insights-audit-log'],
    queryFn: () => api.getInsightsAuditLog({ limit: 50 }),
    enabled: activeTab === 'audit',
  });

  const tabs = [
    { id: 'gamification' as Tab, name: 'Gamification', icon: Trophy },
    { id: 'engagement' as Tab, name: 'Engagement', icon: Users },
    { id: 'audit' as Tab, name: 'Audit Log', icon: FileText },
  ];

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors
                  ${activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                <tab.icon size={18} />
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'gamification' && (
            <GamificationTab data={gamification} isLoading={gamificationLoading} />
          )}
          {activeTab === 'engagement' && (
            <EngagementTab data={engagement} isLoading={engagementLoading} />
          )}
          {activeTab === 'audit' && (
            <AuditTab data={auditLog} isLoading={auditLoading} />
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================
// Type Definitions
// ============================================
interface LeaderboardUser {
  email: string;
  rank: number;
  level: number;
  totalScans: number;
  streakDays: number;
  xp: number;
}

interface LevelDistribution {
  level: number;
  count: number;
}

interface StreakDistribution {
  range: string;
  count: number;
}

interface BadgeDistribution {
  name: string;
  count: number;
}

interface GamificationData {
  summary?: {
    totalXp?: number;
    avgXp?: number;
    usersWithXp?: number;
    totalBadgesEarned?: number;
    usersWithStreaks?: number;
  };
  leaderboard?: LeaderboardUser[];
  levelDistribution?: LevelDistribution[];
  streakDistribution?: StreakDistribution[];
  badgeDistribution?: BadgeDistribution[];
}

interface DailyContribution {
  date: string;
  count: number;
}

interface TopContributor {
  userId: string;
  email: string;
  count: number;
}

interface CountryData {
  country: string;
  count: number;
}

interface EngagementData {
  metrics?: {
    dau?: number;
    dauYesterday?: number;
    wau?: number;
    mau?: number;
    totalUsers?: number;
    dauMauRatio?: number;
    contributorRate?: number;
    uniqueContributors?: number;
    retentionRate?: number;
  };
  dailyContributions?: DailyContribution[];
  topContributors?: TopContributor[];
  usersByCountry?: CountryData[];
}

interface AuditLogEntry {
  id: string;
  userId: string;
  userEmail: string;
  action: string;
  changedBy: string;
  adminEmail: string;
  oldValue: unknown;
  newValue: unknown;
  reason: string | null;
  createdAt: string;
}

interface AuditData {
  logs?: AuditLogEntry[];
  total?: number;
}

// ============================================
// GAMIFICATION TAB
// ============================================
function GamificationTab({ data, isLoading }: { data: GamificationData | undefined; isLoading: boolean }) {
  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (!data) {
    return <EmptyState message="No gamification data available" />;
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <SummaryCard
          icon={<Star className="text-yellow-500" />}
          label="Total XP Earned"
          value={data.summary?.totalXp?.toLocaleString() || 0}
        />
        <SummaryCard
          icon={<TrendingUp className="text-blue-500" />}
          label="Avg XP per User"
          value={data.summary?.avgXp?.toLocaleString() || 0}
        />
        <SummaryCard
          icon={<Users className="text-green-500" />}
          label="Active Gamers"
          value={data.summary?.usersWithXp || 0}
        />
        <SummaryCard
          icon={<Award className="text-purple-500" />}
          label="Badges Earned"
          value={data.summary?.totalBadgesEarned || 0}
        />
        <SummaryCard
          icon={<Flame className="text-orange-500" />}
          label="Active Streaks"
          value={data.summary?.usersWithStreaks || 0}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Leaderboard */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Trophy size={20} className="text-yellow-500" />
            Top 20 Leaderboard
          </h3>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {data.leaderboard?.map((user, index: number) => (
              <div
                key={user.email}
                className={`flex items-center gap-3 p-3 rounded-lg ${
                  index < 3 ? 'bg-gradient-to-r from-yellow-50 to-amber-50' : 'bg-white'
                }`}
              >
                <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                  index === 0 ? 'bg-yellow-400 text-yellow-900' :
                  index === 1 ? 'bg-gray-300 text-gray-700' :
                  index === 2 ? 'bg-amber-600 text-amber-100' :
                  'bg-gray-100 text-gray-600'
                }`}>
                  {user.rank}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{user.email}</p>
                  <p className="text-xs text-gray-500">
                    Level {user.level} • {user.totalScans} scans • {user.streakDays}d streak
                  </p>
                </div>
                <span className="text-sm font-bold text-primary-600">{user.xp.toLocaleString()} XP</span>
              </div>
            ))}
          </div>
        </div>

        {/* Distributions */}
        <div className="space-y-6">
          {/* Level Distribution */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Level Distribution</h3>
            <div className="space-y-2">
              {data.levelDistribution?.map((level) => (
                <div key={level.level} className="flex items-center gap-3">
                  <span className="w-16 text-sm text-gray-600">Level {level.level}</span>
                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-primary-500 h-2 rounded-full"
                      style={{
                        width: `${Math.min(100, (level.count / Math.max(...(data.levelDistribution?.map((l) => l.count) || [1]))) * 100)}%`,
                      }}
                    />
                  </div>
                  <span className="w-12 text-sm text-gray-600 text-right">{level.count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Streak Distribution */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Streak Distribution</h3>
            <div className="space-y-2">
              {data.streakDistribution?.map((streak) => (
                <div key={streak.range} className="flex items-center gap-3">
                  <span className="w-16 text-sm text-gray-600">{streak.range} days</span>
                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-orange-500 h-2 rounded-full"
                      style={{
                        width: `${Math.min(100, (streak.count / Math.max(...(data.streakDistribution?.map((s) => s.count) || [1]), 1)) * 100)}%`,
                      }}
                    />
                  </div>
                  <span className="w-12 text-sm text-gray-600 text-right">{streak.count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Badge Distribution */}
          {data.badgeDistribution && data.badgeDistribution.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Top Badges</h3>
              <div className="flex flex-wrap gap-2">
                {data.badgeDistribution.slice(0, 10).map((badge) => (
                  <span
                    key={badge.name}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-700 text-sm rounded-full"
                  >
                    <Award size={14} />
                    {badge.name}
                    <span className="font-semibold">({badge.count})</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================
// ENGAGEMENT TAB
// ============================================
function EngagementTab({ data, isLoading }: { data: EngagementData | undefined; isLoading: boolean }) {
  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (!data) {
    return <EmptyState message="No engagement data available" />;
  }

  const { metrics, dailyContributions, topContributors, usersByCountry } = data;

  return (
    <div className="space-y-6">
      {/* Metrics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard
          label="DAU (Today)"
          value={metrics?.dau || 0}
          change={(metrics?.dau || 0) - (metrics?.dauYesterday || 0)}
          subtext="vs yesterday"
        />
        <MetricCard
          label="WAU (7 days)"
          value={metrics?.wau || 0}
        />
        <MetricCard
          label="MAU (30 days)"
          value={metrics?.mau || 0}
        />
        <MetricCard
          label="DAU/MAU Ratio"
          value={`${metrics?.dauMauRatio || 0}%`}
          subtext="Stickiness"
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard
          label="Total Users"
          value={metrics?.totalUsers || 0}
        />
        <MetricCard
          label="Contributors (30d)"
          value={metrics?.uniqueContributors || 0}
        />
        <MetricCard
          label="Contributor Rate"
          value={`${metrics?.contributorRate || 0}%`}
          subtext="of total users"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Contributions Chart */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <BarChart3 size={16} />
            Daily Contributions (Last 7 Days)
          </h3>
          <div className="space-y-2">
            {dailyContributions?.map((day) => {
              const max = Math.max(...(dailyContributions?.map((d) => d.count) || [1]), 1);
              return (
                <div key={day.date} className="flex items-center gap-3">
                  <span className="w-20 text-sm text-gray-600">{day.date}</span>
                  <div className="flex-1 bg-gray-200 rounded-full h-3">
                    <div
                      className="bg-green-500 h-3 rounded-full"
                      style={{ width: `${(day.count / max) * 100}%` }}
                    />
                  </div>
                  <span className="w-12 text-sm font-medium text-gray-900 text-right">{day.count}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top Contributors */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Activity size={16} />
            Top Contributors (30 Days)
          </h3>
          <div className="space-y-2">
            {topContributors?.slice(0, 10).map((user, index: number) => (
              <div key={user.userId} className="flex items-center gap-3 p-2 bg-white rounded">
                <span className="w-6 text-sm font-medium text-gray-500">{index + 1}.</span>
                <span className="flex-1 text-sm text-gray-900 truncate">{user.email}</span>
                <span className="text-sm font-semibold text-green-600">{user.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Users by Country */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <Globe size={16} />
          Users by Country
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
          {usersByCountry?.slice(0, 12).map((country) => (
            <div key={country.country} className="bg-white p-3 rounded-lg text-center">
              <span className="text-lg font-bold text-gray-900">{country.count}</span>
              <p className="text-xs text-gray-500 uppercase">{country.country || 'Unknown'}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================
// AUDIT TAB
// ============================================
function AuditTab({ data, isLoading }: { data: AuditData | undefined; isLoading: boolean }) {
  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (!data?.logs?.length) {
    return <EmptyState message="No audit log entries found" />;
  }

  return (
    <div className="space-y-4">
      <div className="text-sm text-gray-500 mb-4">
        Showing {data.logs.length} of {data.total} entries
      </div>

      <div className="space-y-3">
        {data.logs?.map((log) => (
          <div key={log.id} className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                    log.action.includes('grant') ? 'bg-green-100 text-green-700' :
                    log.action.includes('revoke') ? 'bg-red-100 text-red-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {log.action}
                  </span>
                  <span className="text-sm text-gray-900 font-medium truncate">{log.userEmail}</span>
                </div>
                <p className="text-sm text-gray-600">
                  Changed by: <span className="font-medium">{log.adminEmail}</span>
                </p>
                {log.reason && (
                  <p className="text-sm text-gray-500 mt-1">Reason: {log.reason}</p>
                )}
                {(log.oldValue !== undefined || log.newValue !== undefined) && (
                  <div className="mt-2 text-xs font-mono bg-gray-100 rounded p-2 overflow-x-auto">
                    {log.oldValue !== undefined && <div className="text-red-600">- {JSON.stringify(log.oldValue)}</div>}
                    {log.newValue !== undefined && <div className="text-green-600">+ {JSON.stringify(log.newValue)}</div>}
                  </div>
                )}
              </div>
              <TimeAgo timestamp={log.createdAt} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================
// SHARED COMPONENTS
// ============================================

function SummaryCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string | number }) {
  return (
    <div className="bg-gray-50 rounded-lg p-4 text-center">
      <div className="flex justify-center mb-2">{icon}</div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-xs text-gray-500">{label}</p>
    </div>
  );
}

function MetricCard({ label, value, change, subtext }: { label: string; value: string | number; change?: number; subtext?: string }) {
  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
      <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
      {change !== undefined && (
        <p className={`text-sm ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
          {change >= 0 ? '+' : ''}{change} {subtext}
        </p>
      )}
      {subtext && change === undefined && (
        <p className="text-xs text-gray-500">{subtext}</p>
      )}
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="animate-pulse">
          <div className="h-24 bg-gray-200 rounded-lg"></div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="text-center py-12">
      <p className="text-gray-500">{message}</p>
    </div>
  );
}
